import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { userLogin } from "../../redux/actions/userAction";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./login.css";

// ─── Theme tokens ────────────────────────────────────────────────
const NAVY_DEEP = "#0a1628";
const NAVY_MID = "#142440";
const NAVY_SOFT = "#1a2d4a";
const TEAL = "#4CCEAC";
const INDIGO = "#5B5FEF";
const TEXT_DARK = "#0d1b2a";
const TEXT_MUTED = "#6b7280";
const SURFACE_LIGHT = "rgba(255,255,255,0.04)";
const BORDER_LIGHT = "rgba(255,255,255,0.08)";

// ─── Styled components ───────────────────────────────────────────

const PageWrapper = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  overflow: "hidden",
});

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: "0 0 40%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "32px 40px",
  background: `linear-gradient(140deg, ${NAVY_DEEP} 0%, ${NAVY_MID} 55%, ${NAVY_SOFT} 100%)`,
  color: "#fff",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    width: 520,
    height: 520,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(76,206,172,0.10) 0%, transparent 70%)",
    bottom: -180,
    left: -120,
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(91,95,239,0.08) 0%, transparent 70%)",
    top: -100,
    right: -80,
    pointerEvents: "none",
  },

  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const ControlTowerChip = styled(Box)({
  position: "absolute",
  top: 20,
  left: 40,
  padding: "4px 12px",
  border: `1px solid ${TEAL}`,
  borderRadius: 999,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: "1px",
  color: TEAL,
  zIndex: 2,
  textTransform: "uppercase",
});

const FeatureList = styled(Box)({
  position: "relative",
  zIndex: 1,
});

const StatGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
  marginTop: 20,
  marginBottom: 22,
  position: "relative",
  zIndex: 1,
});

const StatCard = styled(Box)({
  background: SURFACE_LIGHT,
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: 10,
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const StatIcon = styled("span")({
  fontSize: 13,
  lineHeight: 1,
  marginBottom: 2,
});

const StatValue = styled(Typography)({
  fontSize: 16,
  fontWeight: 700,
  color: TEAL,
  lineHeight: 1.1,
});

const StatLabel = styled(Typography)({
  fontSize: 10,
  color: "rgba(255,255,255,0.6)",
  fontWeight: 400,
});

const FeatureRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 14,
  position: "relative",
  zIndex: 1,
  marginBottom: 18,
});

const FeatureItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const FeatureTitle = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#fff",
});

const FeatureDesc = styled(Typography)({
  fontSize: 9.5,
  color: "rgba(255,255,255,0.5)",
  lineHeight: 1.4,
});

const PillRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  position: "relative",
  zIndex: 1,
});

const Pill = styled("span")({
  padding: "3px 9px",
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: 999,
  fontSize: 9.5,
  fontWeight: 500,
  color: "rgba(255,255,255,0.7)",
  background: SURFACE_LIGHT,
});

const RightPanel = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  padding: "40px 24px",
  overflowY: "auto",

  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "32px 20px",
    alignItems: "flex-start",
    paddingTop: 56,
  },
}));

const FormContainer = styled(Box)({
  width: "100%",
  maxWidth: 340,
});

const MobileLogo = styled("img")(({ theme }) => ({
  height: 40,
  width: "auto",
  objectFit: "contain",
  display: "none",
  marginBottom: 32,

  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));

const PrimaryButton = styled(Button)({
  width: "100%",
  height: 38,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 12.5,
  textTransform: "none",
  background: NAVY_DEEP,
  color: "#fff",
  boxShadow: "none",
  letterSpacing: "0.2px",
  "&:hover": {
    background: NAVY_MID,
    boxShadow: "0 4px 14px rgba(10,22,40,0.25)",
  },
  "&:disabled": {
    background: "#e5e7eb",
    color: "#9ca3af",
  },
});

const SecondaryButton = styled(Button)({
  width: "100%",
  height: 38,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 12.5,
  textTransform: "none",
  background: "#fff",
  color: INDIGO,
  border: `1.5px solid ${INDIGO}`,
  boxShadow: "none",
  letterSpacing: "0.2px",
  "&:hover": {
    background: "rgba(91,95,239,0.04)",
    borderColor: INDIGO,
    boxShadow: "none",
  },
});

const ForgotLink = styled(Button)({
  fontSize: 11.5,
  fontWeight: 500,
  color: INDIGO,
  textTransform: "none",
  padding: 0,
  minWidth: 0,
  "&:hover": {
    textDecoration: "underline",
    background: "none",
  },
});

// ─── Component ───────────────────────────────────────────────────

export default function Login() {
  const { register, handleSubmit } = useForm();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const { loading, error } = useSelector((state) => state.user);
  const { enqueueSnackbar } = useSnackbar();
  const environment = process.env.REACT_APP_ENV;

  const onSubmit = (data) => {
    data.panel = "ADMIN";
    dispatch(userLogin(data));
  };

  if (error === undefined) {
    enqueueSnackbar("Something went wrong", { variant: "error" });
  } else if (error) {
    if (typeof error === "string" && error !== "Incorrect username or password") {
      enqueueSnackbar(error, { variant: "error" });
    }
  }

  const handleGoogleSignIn = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI;
    const scope = process.env.REACT_APP_GOOGLE_SCOPE;
    const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline`;
    window.location.href = googleUrl;
  };

  const stats = [
    { icon: "🏭", value: "52+", label: "Fulfilment Centers" },
    { icon: "📦", value: "3M+", label: "Sq. Ft. Warehouse Space" },
    { icon: "📍", value: "24,000+", label: "Pin Codes Served" },
    { icon: "🔗", value: "50+", label: "Marketplace Integrations" },
  ];

  const features = [
    { title: "Warehousing", desc: "Multi-channel, tech-enabled facilities" },
    { title: "Logistics", desc: "Nationwide express & same-day delivery" },
    { title: "Technology", desc: "Enterprise-grade supply chain stack" },
  ];

  const pills = ["Pay-Per-Use", "SLA-Compliant", "Multi-Channel", "Tech-Enabled", "Pan-India"];

  return (
    <PageWrapper>
      {/* ── Left branding panel ── */}
      <LeftPanel>
        <ControlTowerChip>Proship Admin Panel</ControlTowerChip>

        <FeatureList>
          <Typography
            sx={{
              fontSize: { md: 19, lg: 22 },
              fontWeight: 700,
              lineHeight: 1.25,
              mb: 1,
              color: "#fff",
            }}>
            Your E2E <Box component="span" sx={{ color: TEAL }}>Supply Chain</Box> Partner
          </Typography>

          <Typography
            sx={{
              fontSize: 11.5,
              color: "rgba(255,255,255,0.55)",
              lineHeight: 1.5,
            }}>
            Enterprise-grade, SLA-compliant warehousing & fulfilment for B2B, B2C, D2C and Omnichannel brands.
          </Typography>

          <StatGrid>
            {stats.map((s) => (
              <StatCard key={s.label}>
                <StatIcon>{s.icon}</StatIcon>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </StatCard>
            ))}
          </StatGrid>

          <FeatureRow>
            {features.map((f) => (
              <FeatureItem key={f.title}>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureItem>
            ))}
          </FeatureRow>

          <PillRow>
            {pills.map((p) => (
              <Pill key={p}>{p}</Pill>
            ))}
          </PillRow>
        </FeatureList>
      </LeftPanel>

      {/* ── Right login panel ── */}
      <RightPanel>
        <FormContainer>
          <MobileLogo src="/img/prozo-logo2.svg" alt="Prozo logo" />

          <Typography sx={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, lineHeight: 1.3, mb: 0.25 }}>
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: TEXT_MUTED, mb: 2.5 }}>
            Sign in to Proship Admin Panel
          </Typography>

          {environment !== "prod" && (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    {...register("username", {
                      required: "Email is required",
                      value: "",
                      pattern: {
                        value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: "Please enter a valid email",
                      },
                    })}
                    className="login-input"
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Email Address"
                    name="username"
                    autoFocus
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                      "& .MuiInputLabel-root": { fontSize: 13 },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    {...register("password", { required: true, value: "" })}
                    className="login-input"
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                      "& .MuiInputLabel-root": { fontSize: 13 },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((p) => !p)}
                            edge="end"
                            size="small">
                            {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {error === "Incorrect username or password" && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="body2">{error}</Typography>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ pt: "12px !important" }}>
                  <PrimaryButton type="submit" disabled={loading} variant="contained">
                    {loading ? (
                      <><CircularProgress size={16} sx={{ color: "#9ca3af", mr: 1 }} /> Signing in…</>
                    ) : "Login with Password"}
                  </PrimaryButton>
                </Grid>
              </Grid>
            </Box>
          )}

          <div className="or-divider"><span>OR</span></div>

          <SecondaryButton variant="outlined" onClick={handleGoogleSignIn}>
            Sign in with Prozo Email ID
          </SecondaryButton>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
            <ForgotLink variant="text" component={Link} to="/forgot-password">
              Forgot Password?
            </ForgotLink>
          </Box>
        </FormContainer>
      </RightPanel>
    </PageWrapper>
  );
}
